import React from 'react';
import { Link } from 'react-router-dom';
import { PATHS } from '@/routes/paths';
import { useSession } from '@/hooks/useSession';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';
import { checkPermission } from '@/hooks/usePermission';
import { canViewExercicios } from '@/lib/teacher-profile';
import { DashboardSection } from '@/components/custom/dashboard';
import desafioAoVivoIcon from '@/assets/images/icons/challenge-bolt-3d.png';
import desafioDaSemanaIcon from '@/assets/images/icons/desafio-da-semana.png';
import exerciciosIcon from '@/assets/images/icons/exercicios.png';
import planoIcon from '@/assets/images/icons/books-3d.png';
import atividadesIcon from '@/assets/images/icons/atividade-complementar.png';

// Mesma técnica de bevel 3D do botão-seta do HomeNavCard (box-shadow, não
// border-b-3 — esse token não existe no repo).
//
// 5 famílias de cor bem distintas entre si (não só tons diferentes do mesmo
// laranja) — desafioAoVivo/desafioDaSemana/atividades antes ficavam todas no
// mesmo tom pastel quente e pareciam iguais lado a lado. `merit` (rosa) vem
// do tailwind.config.js (não tem var CSS própria, só existe como classe
// `bg-merit`/`bg-merit-light`); os demais já são tokens de `theme.playful.sass`.
const SHORTCUT_ACCENTS = {
  desafioAoVivo: { bg: 'var(--color-bg-mission-soft)', button: 'var(--color-mission)' }, // laranja
  desafioDaSemana: { bg: '#FFE7E5', button: '#FF4C6F' }, // rosa (merit)
  exercicios: { bg: 'var(--color-bg-teal-soft)', button: 'var(--color-third)' }, // ciano
  planoDeAula: { bg: 'var(--color-bg-lilac-soft)', button: 'var(--color-secondary)' }, // lilás
  atividades: { bg: 'color-mix(in srgb, var(--color-badge-gold) 30%, white)', button: 'var(--color-badge-gold-text)' } // dourado
};

function ShortcutActionCard({ to, illustration, title, subtitle, ctaLabel, accent }) {
  return (
    <div
      className="flex flex-col items-start gap-2 rounded-[16px] p-3"
      style={{ background: accent.bg }}
      data-testid="shortcut-card"
      data-title={title}
    >
      <img
        src={illustration}
        alt=""
        aria-hidden="true"
        className="h-12 w-12 object-contain drop-shadow-sm"
      />
      <div className="flex flex-col gap-0.5">
        <span
          className="block text-sm font-extrabold"
          style={{ color: '#222731' }}
        >
          {title}
        </span>
        <span className="line-clamp-2 block text-sm font-semibold text-neutral-600">{subtitle}</span>
      </div>

      <Link
        to={to}
        className="mt-1 flex h-8 shrink-0 items-center justify-center rounded-full px-3 text-xs font-bold text-white transition-transform duration-100 active:scale-[0.97]"
        style={{
          background: accent.button,
          boxShadow: `0 3px 0 color-mix(in srgb, ${accent.button} 72%, #000), 0 4px 8px rgba(0,0,0,0.18)`
        }}
      >
        {ctaLabel}
      </Link>
    </div>
  );
}

export function TeacherShortcuts() {
  const { session } = useSession();
  const { abilities } = usePlatformAbilities();

  // Mesma regra do item "Exercícios" em menu-admin.jsx, agora numa fonte
  // única (src/lib/teacher-profile.js) — não pode mais divergir do menu.
  const canSeeExercicios = canViewExercicios(session, abilities, checkPermission);

  const shortcuts = [
    {
      key: 'desafio-ao-vivo',
      to: PATHS.LIVE_CHALLENGE.ADMIN_CREATE('nome'),
      illustration: desafioAoVivoIcon,
      title: 'Desafio Ao Vivo',
      subtitle: 'Conduza disputas em tempo real',
      ctaLabel: 'Criar',
      accent: SHORTCUT_ACCENTS.desafioAoVivo,
      available: Boolean(abilities?.['desafio-ao-vivo'])
    },
    {
      key: 'desafio-da-semana',
      to: PATHS.WEEK_CHALLENGE.CREATE,
      illustration: desafioDaSemanaIcon,
      title: 'Desafio da Semana',
      subtitle: 'Lance o desafio e aguarde os resultados',
      ctaLabel: 'Criar',
      accent: SHORTCUT_ACCENTS.desafioDaSemana,
      available: Boolean(abilities?.['desafio-da-semana'])
    },
    {
      key: 'planos-de-aula',
      to: PATHS.LESSON_PLAN.START,
      illustration: planoIcon,
      title: 'Planos de Aula',
      subtitle: 'Monte e reaproveite seus planos',
      ctaLabel: 'Ver',
      accent: SHORTCUT_ACCENTS.planoDeAula,
      available: true
    },
    {
      key: 'atividades-complementares',
      to: PATHS.COMPLEMENTARY_ACTIVITY.LIST,
      illustration: atividadesIcon,
      title: 'Atividades complementares',
      subtitle: 'Ofereça atividades de estudo individuais',
      ctaLabel: 'Ver',
      accent: SHORTCUT_ACCENTS.atividades,
      available: Boolean(abilities?.['cursos-ativ-complementar'])
    }
  ].filter(shortcut => shortcut.available);

  if (shortcuts.length === 0) return null;

  return (
    <DashboardSection>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {shortcuts.map(shortcut => (
          <ShortcutActionCard
            key={shortcut.key}
            to={shortcut.to}
            illustration={shortcut.illustration}
            title={shortcut.title}
            subtitle={shortcut.subtitle}
            ctaLabel={shortcut.ctaLabel}
            accent={shortcut.accent}
          />
        ))}
      </div>
    </DashboardSection>
  );
}
